import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FC } from 'react';
import { DeviceFunctionResponse } from '@/redux/api/endpoints/device-functions';
import { cx } from '@/utils/cx';
import { describeAlexaApiError } from './describe-api-error';

type AlexaFunctionResultProps = {
  data?: DeviceFunctionResponse;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
};

const formatPayload = (payload: unknown): string => {
  if (typeof payload === 'string') {
    return payload;
  }

  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
};

const RawPayload: FC<{ payload: unknown }> = ({ payload }) => (
  <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-gray-900 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-gray-100">
    {formatPayload(payload)}
  </pre>
);

export const AlexaFunctionResult: FC<AlexaFunctionResultProps> = ({
  data,
  error,
  isLoading,
}) => {
  if (isLoading) {
    return <p className="mt-3 text-sm text-gray-500">Sending request…</p>;
  }

  if (error) {
    const description = describeAlexaApiError(error);
    return (
      <details
        open
        className="mt-3 rounded-md border border-red-200 bg-red-50 p-3"
      >
        <summary className="cursor-pointer text-sm font-semibold text-red-700">
          {description.title}
        </summary>
        <p className="mt-2 text-sm text-red-700">{description.detail}</p>
        {description.payload !== undefined && (
          <RawPayload payload={description.payload} />
        )}
      </details>
    );
  }

  if (!data) {
    return null;
  }

  const success = data.status >= 200 && data.status < 300;
  return (
    <details
      open
      className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3"
    >
      <summary className="cursor-pointer text-sm font-medium text-gray-700">
        Amazon response{' '}
        <span
          className={cx(
            'ml-1 rounded-full px-2 py-0.5 text-xs font-semibold',
            success
              ? 'bg-successTint text-successShade'
              : 'bg-warningTint text-warningShade',
          )}
        >
          {data.status} {success ? 'OK' : 'Error'}
        </span>
      </summary>
      <RawPayload payload={data.body} />
    </details>
  );
};
